import { useRef, useState } from 'react';
import type { NotificationSeverity } from '@nadoutong/contracts';
import {
  NOTIFICATION_ANNOUNCE_FINGERPRINT_NAMESPACE,
  NOTIFICATION_ANNOUNCE_TEST_RECIPIENTS_MAX,
} from '@nadoutong/contracts';
import { OpsApiError } from '../../api/client.ts';
import { useAnnounceNotification } from '../../api/useNotificationAnnouncements.ts';
import { coerceErrorMessage, useToast } from '../Toast.tsx';
import { useT } from '../../i18n/index.ts';
import { Button, Dialog, FormField, Textarea } from '../../ui/index.ts';
import type { AnnouncementSendResult } from './announcementSendResult.ts';

/**
 * The whitelist test send (#1965) — show a draft to a named handful of orgs
 * before the fleet sees it.
 *
 * ── WHY THE RECIPIENT LIST LIVES IN THIS COMPONENT, AND NOWHERE ELSE ────────
 * #1945 was an audience selector left set: an operator picked a brand, believed
 * they had published to everyone, and reached 9 of 592 orgs. Re-introducing any
 * recipient control re-introduces that shape unless the control CANNOT be left
 * set, so the defence here is structural rather than procedural:
 *
 *   - the list is `useState` inside this component, and the page renders this
 *     component only while the dialog is open. Closing it UNMOUNTS the state.
 *     There is no "clear the field" step for an operator to forget, and no
 *     effect that has to remember to run.
 *   - the page's fleet-publish path holds no whitelist variable at all — it
 *     cannot read this state even by mistake, because the state does not exist
 *     outside this subtree. That is what makes "the whitelist can never become
 *     the default audience" a property of the code rather than a habit.
 *   - an empty list cannot be submitted from here (the button stays disabled),
 *     and if one ever were, the api wrapper relays `[]` as `[]` and the platform
 *     route refuses it. TWO INDEPENDENT GATES, and the second is only real
 *     because nothing between them translates `[]` into "no filter" — a wrapper
 *     that dropped the key on an empty array would turn the backstop into a
 *     fleet-wide broadcast, which is the failure both gates exist for and the
 *     one with no undo.
 *
 * ── NO TYPE-TO-CONFIRM, DELIBERATELY ───────────────────────────────────────
 * The fleet publish next door demands the slug be typed back, because its blast
 * radius is every customer and its cost is unrecallable. A test send's radius is
 * the list in front of the operator, which the dialog shows parsed and counted.
 * Adding friction to the SAFE action is how a surface teaches operators to skip
 * the drafting step and publish straight to the fleet — the exact behaviour
 * #1965 exists to make unnecessary.
 *
 * ── IT IS THE SAME SLUG AND THE SAME FINGERPRINT ───────────────────────────
 * Not an `announce-test:` namespace: the later fleet publish of this slug
 * upgrades the whitelisted org's row in place, so the person who proof-read the
 * draft holds one notification carrying the final copy rather than two
 * unrelated messages. The dialog says so, because "the recipient will see this
 * twice" would otherwise be a reasonable thing for an operator to assume.
 */

interface AnnouncementDraft {
  readonly slug: string;
  readonly title: string;
  readonly description: string;
  readonly severity: NotificationSeverity;
}

interface AnnouncementTestSendDialogProps {
  readonly draft: AnnouncementDraft;
  readonly onClose: () => void;
  readonly onSent: (result: AnnouncementSendResult) => void;
}

const ORGS_FIELD_ID = 'announcement-test-org-ids';

/**
 * One recipient per line — an EMAIL or an org id (#1968) — and commas too, so
 * an operator pasting from a spreadsheet cell or a chat message does not have
 * to reformat.
 *
 * Deduplicated, with emails folded case-insensitively because one mailbox typed
 * two ways is one recipient. The count this produces is what the dialog shows,
 * and it counts ENTRIES, not orgs: two colleagues in one org are two entries
 * and one org, and only the server can know that. Which is why the dialog says
 * "recipients" and the result panel reports the org count the server wrote —
 * guessing here would overstate reach on the one surface built to stop reach
 * being overstated.
 */
export function parseTestRecipientList(raw: string): readonly string[] {
  const seen = new Set<string>();
  const entries: string[] = [];
  for (const candidate of raw.split(/[\s,]+/)) {
    const entry = candidate.trim();
    if (entry.length === 0) continue;
    const key = entry.includes('@') ? entry.toLowerCase() : entry;
    if (seen.has(key)) continue;
    seen.add(key);
    entries.push(entry);
  }
  return entries;
}

export function AnnouncementTestSendDialog({
  draft,
  onClose,
  onSent,
}: AnnouncementTestSendDialogProps): JSX.Element {
  const t = useT();
  const toast = useToast();
  const announce = useAnnounceNotification();
  const [raw, setRaw] = useState('');
  // `announce.isPending` only becomes true after React has re-rendered, so two
  // clicks inside one frame both pass the disabled check and both send. The
  // second send is idempotent and identical, so the damage is not a duplicate
  // announcement — it is a `resurfaced > 0` on the result panel, which on the
  // test arm now reads as "you overwrote somebody else's slug". A ref flips
  // synchronously and keeps that lie off the screen.
  const inFlight = useRef(false);

  const recipients = parseTestRecipientList(raw);
  const overLimit = recipients.length > NOTIFICATION_ANNOUNCE_TEST_RECIPIENTS_MAX;
  const sendable = recipients.length > 0 && !overLimit && !announce.isPending;

  function send(): void {
    if (!sendable || inFlight.current) return;
    inFlight.current = true;
    announce.mutate(
      {
        slug: draft.slug,
        title: draft.title,
        description: draft.description,
        severity: draft.severity,
        test_recipients: recipients,
      },
      {
        onSuccess: (response) => {
          onSent({
            fingerprint: response.fingerprint,
            requestedMode: 'whitelist',
            requestedOrgCount: recipients.length,
            // Recorded as the SERVER reported it, never assumed from the
            // request — the result panel's job is to notice a disagreement.
            mode: response.mode,
            orgsTargeted: response.orgs_targeted,
            created: response.created,
            resurfaced: response.resurfaced,
          });
          onClose();
        },
        onError: (err) => {
          // Kept OPEN on failure, unlike the fleet confirm dialog. Nothing was
          // written, the list is still on screen, and the likely fix is editing
          // one id in it — closing would throw the operator's paste away.
          toast.error(
            err instanceof OpsApiError && err.code === 'INTERNAL'
              ? t('announcements.toast.failed')
              : coerceErrorMessage(err),
          );
        },
        // Released on both endings. On success this component is about to
        // unmount, so the reset is only for the failure path — where the dialog
        // stays open with the list intact and the operator is expected to fix an
        // id and send again.
        onSettled: () => {
          inFlight.current = false;
        },
      },
    );
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={t('announcements.test.dialog.title')}
      description={t('announcements.test.dialog.body', {
        fingerprint: `${NOTIFICATION_ANNOUNCE_FINGERPRINT_NAMESPACE}${draft.slug}`,
      })}
      closeLabel={t('common.close')}
      testId="announcements-test-dialog"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={announce.isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={send}
            disabled={!sendable}
            loading={announce.isPending}
            data-testid="announcements-test-send"
          >
            {announce.isPending
              ? t('announcements.test.send.pending')
              : t('announcements.test.send', { count: String(recipients.length) })}
          </Button>
        </>
      }
    >
      <FormField
        label={t('announcements.test.field.orgs')}
        fieldId={ORGS_FIELD_ID}
        required
        hint={t('announcements.test.field.orgs.hint', {
          max: String(NOTIFICATION_ANNOUNCE_TEST_RECIPIENTS_MAX),
        })}
        error={
          overLimit
            ? t('announcements.test.field.orgs.over_limit', {
                max: String(NOTIFICATION_ANNOUNCE_TEST_RECIPIENTS_MAX),
              })
            : undefined
        }
      >
        <Textarea
          id={ORGS_FIELD_ID}
          mono
          rows={5}
          value={raw}
          invalid={overLimit}
          aria-describedby={overLimit ? `${ORGS_FIELD_ID}-error` : undefined}
          placeholder={t('announcements.test.field.orgs.placeholder')}
          onChange={(event) => setRaw(event.target.value)}
        />
      </FormField>
      {/* The parsed count, not the raw text: what the button will actually do.
          An operator who pasted a trailing comma or a duplicate line needs to
          see the number the server will agree with BEFORE sending, because
          afterwards there is no unsend. */}
      <p className="mt-3 mb-0 text-dense text-paper-mid" data-testid="announcements-test-count">
        {recipients.length === 0
          ? t('announcements.test.field.orgs.empty')
          : t('announcements.test.field.orgs.count', { count: String(recipients.length) })}
      </p>
    </Dialog>
  );
}
